import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { X, ShoppingCart, Trash2, Plus, Minus, Loader2, Lock, Crown, Truck, Users, RefreshCw, PartyPopper, Gift } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { useCart } from "../lib/CartContext";
import { useAuth } from "../lib/AuthContext";
import PayPalCartCheckout from "./PayPalCartCheckout";

export default function CartDrawer() {
  const { remove, setQty, membership, gifts, giftSubtotal, giftCount, shopItems, shopSubtotal, shopCount, dueToday, open, setOpen } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [quote, setQuote] = useState(null);

  const hasMembership = !!membership;
  const hasGifts = gifts.length > 0;

  useEffect(() => {
    if (!open || !shopItems.length) { setQuote(null); return; }
    let active = true;
    const t = setTimeout(() => {
      api.post("/payments/cart/quote", { items: shopItems.map((i) => ({ sku: i.sku, quantity: i.qty })), origin_url: window.location.origin })
        .then(({ data }) => { if (active) setQuote(data); })
        .catch(() => { if (active) setQuote(null); });
    }, 250);
    return () => { active = false; clearTimeout(t); };
  }, [open, shopItems]);

  // ONE unified checkout: membership + books + gifts → single Stripe payment. Books/gifts-only → guest OK.
  const checkout = async () => {
    if (!shopItems.length && !hasMembership && !hasGifts) return;
    if (hasMembership && !user) {
      try { localStorage.setItem("sg_open_cart", "1"); } catch {}
      setOpen(false);
      toast.message("Create or sign in to your Kingdom account to activate your membership. Your cart is saved. 💛");
      nav("/kingdom/login?mode=signup");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/payments/cart/checkout", {
        items: shopItems.map((i) => ({ sku: i.sku, quantity: i.qty })),
        gift_items: gifts.map((g) => ({ lookup_key: g.lookupKey, quantity: g.qty, recipient: g.recipient || null, sender: g.sender || null, message: g.message || null })),
        membership_lookup_key: hasMembership ? membership.lookupKey : null,
        origin_url: window.location.origin,
      });
      if (!data?.checkout_url) throw new Error("No checkout URL returned");
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Could not start checkout. Please try again."); setBusy(false); }
  };

  const onPaypalSuccess = (orderNumber, sessionId) => {
    [...shopItems, ...gifts].forEach((i) => remove(i.sku)); setOpen(false);
    const qs = new URLSearchParams({ provider: "paypal" });
    if (sessionId) qs.set("session_id", sessionId);
    else if (orderNumber) qs.set("order", orderNumber);
    nav(`/payment/success?${qs.toString()}`);
  };

  const isEmpty = !membership && shopItems.length === 0 && !hasGifts;
  const displayItems = [...gifts, ...shopItems];
  const intervalShort = membership ? (membership.interval === "year" ? "yr" : "mo") : "";

  return (
    <div className={`fixed inset-0 z-[60] ${open ? "" : "pointer-events-none"}`} data-testid="cart-drawer" aria-hidden={!open} inert={!open}>
      <div className={`absolute inset-0 bg-ink/40 backdrop-blur-sm transition-opacity ${open ? "opacity-100" : "opacity-0"}`} onClick={() => setOpen(false)} />
      <aside className={`absolute right-0 top-0 h-full w-full max-w-md bg-cream shadow-2xl flex flex-col transition-transform duration-300 ${open ? "translate-x-0" : "translate-x-full"}`}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-black/5 bg-white">
          <h2 className="font-display font-extrabold text-xl text-ink flex items-center gap-2"><ShoppingCart size={20} className="text-purpleK" /> Your Castle Cart {(shopCount + giftCount + (membership ? 1 : 0)) > 0 && <span className="text-ink/40 text-sm">({shopCount + giftCount + (membership ? 1 : 0)})</span>}</h2>
          <button onClick={() => setOpen(false)} data-testid="cart-close" className="grid place-items-center h-9 w-9 rounded-full hover:bg-cream text-ink/60"><X size={22} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {isEmpty ? (
            <div className="h-full grid place-items-center text-center px-6" data-testid="cart-empty">
              <div>
                <ShoppingCart size={48} className="mx-auto text-ink/20" />
                <p className="font-display font-bold text-ink mt-3">Your cart is empty</p>
                <p className="text-ink/50 text-sm mt-1">Add a storybook, workbook or journal from the Story Castle — or a membership from the Membership page.</p>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {membership && (
                <div className="rounded-2xl bg-white p-4 border-2 border-goldK/40" data-testid="cart-membership">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="grid place-items-center h-10 w-10 rounded-xl bg-goldK/20 text-goldDark shrink-0"><Crown size={20} /></span>
                      <div className="min-w-0">
                        <p className="font-display font-extrabold text-ink text-sm leading-tight">{membership.title}</p>
                        <p className="text-xs text-ink/50 font-semibold">{membership.billingLabel} membership · recurring</p>
                      </div>
                    </div>
                    <button onClick={() => remove(membership.sku)} data-testid="cart-membership-remove" className="text-ink/30 hover:text-pinkK shrink-0"><Trash2 size={16} /></button>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-1.5 text-[12px] text-ink/70">
                    <span className="inline-flex items-center gap-1.5"><Users size={13} className="text-purpleK" /> Up to {membership.childLimit} children</span>
                    <span className="inline-flex items-center gap-1.5"><RefreshCw size={13} className="text-purpleK" /> {membership.renewal}</span>
                  </div>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-xs text-ink/45">Billed {membership.interval === "year" ? "yearly" : "monthly"}</span>
                    <span className="font-display font-extrabold text-ink">${membership.price.toFixed(2)}<span className="text-xs text-ink/50 font-semibold">/{intervalShort}</span></span>
                  </div>
                </div>
              )}

              {displayItems.map((it) => (
                <div key={it.sku} className={`flex gap-3 rounded-2xl bg-white p-3 border ${it.type === "gift" ? "border-pinkK/30" : "border-black/5"}`} data-testid={`cart-item-${it.sku}`}>
                  <div className="h-16 w-16 rounded-xl bg-cream overflow-hidden shrink-0 grid place-items-center">
                    {it.type === "gift" ? <Gift className="text-pinkK" /> : it.cover ? <img src={it.cover} alt={it.title} className="h-full w-full object-cover" /> : <ShoppingCart className="text-ink/20" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-display font-bold text-ink text-sm leading-tight truncate">{it.type === "gift" ? `🎁 Gift — ${it.title}` : it.title}</p>
                    <p className="text-xs text-ink/45">{it.type === "gift" ? "One-time gift · redemption code" : it.type}</p>
                    <div className="flex items-center justify-between mt-1.5">
                      <div className="inline-flex items-center gap-1.5 rounded-full bg-cream px-1.5 py-1">
                        <button onClick={() => setQty(it.sku, it.qty - 1)} data-testid={`cart-dec-${it.sku}`} className="grid place-items-center h-6 w-6 rounded-full bg-white text-ink/60"><Minus size={13} /></button>
                        <span className="w-5 text-center font-display font-bold text-sm" data-testid={`cart-qty-${it.sku}`}>{it.qty}</span>
                        <button onClick={() => setQty(it.sku, it.qty + 1)} data-testid={`cart-inc-${it.sku}`} className="grid place-items-center h-6 w-6 rounded-full bg-white text-ink/60"><Plus size={13} /></button>
                      </div>
                      <span className="font-display font-extrabold text-ink">${(it.price * it.qty).toFixed(2)}</span>
                    </div>
                  </div>
                  <button onClick={() => remove(it.sku)} data-testid={`cart-remove-${it.sku}`} className="self-start text-ink/30 hover:text-pinkK"><Trash2 size={16} /></button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* -------------------- Unified checkout footer -------------------- */}
        {!isEmpty && (
          <div className="border-t border-black/5 bg-white p-5" data-testid="cart-summary">
            {hasMembership ? (
              <>
                <p className="text-xs text-ink/50 font-display font-bold mb-2 flex items-center gap-1.5"><Lock size={13} /> Due today</p>
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between text-sm" data-testid="due-membership">
                    <span className="text-ink/70 flex items-center gap-1.5"><Crown size={14} className="text-goldDark" /> {membership.title}</span>
                    <span className="font-display font-bold text-ink">${membership.price.toFixed(2)}<span className="text-xs text-ink/45 font-semibold">/{intervalShort}</span></span>
                  </div>
                  {shopItems.length > 0 && (
                    <div className="flex items-center justify-between text-sm" data-testid="due-shop">
                      <span className="text-ink/70 flex items-center gap-1.5"><ShoppingCart size={14} className="text-purpleK" /> Kingdom Shop items ({shopCount})</span>
                      <span className="font-display font-bold text-ink">${shopSubtotal.toFixed(2)}</span>
                    </div>
                  )}
                  {giftCount > 0 && (
                    <div className="flex items-center justify-between text-sm" data-testid="due-gift">
                      <span className="text-ink/70 flex items-center gap-1.5"><Gift size={14} className="text-pinkK" /> Gift membership{giftCount > 1 ? "s" : ""} ({giftCount})</span>
                      <span className="font-display font-bold text-ink">${giftSubtotal.toFixed(2)}</span>
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-between pt-2 mt-2 border-t border-black/5">
                  <span className="text-ink/70 font-display font-extrabold">Due today</span>
                  <span className="font-display font-extrabold text-2xl text-ink" data-testid="cart-due-today">${dueToday.toFixed(2)}</span>
                </div>
                <div className="mt-2 rounded-xl bg-goldK/10 border border-goldK/25 p-2.5 text-[12px] text-ink/70 flex items-start gap-2" data-testid="renewal-note">
                  <RefreshCw size={14} className="text-goldDark shrink-0 mt-0.5" />
                  <span>Renews: <b>{membership.title}</b> at ${membership.price.toFixed(2)}/{membership.interval === "year" ? "year" : "month"}. One-time items are charged once. Cancel anytime.</span>
                </div>
                <p className="text-xs text-ink/45 my-2">Shipping &amp; tax calculated at secure checkout.</p>
                {shopItems.length > 0 && (
                  <div className="mb-3 rounded-2xl bg-tealK/10 border border-tealK/25 p-3 flex items-start gap-2.5" data-testid="one-payment-reassurance">
                    <PartyPopper size={18} className="text-tealK shrink-0 mt-0.5" />
                    <p className="text-[12.5px] text-ink/75 leading-snug"><b className="text-ink font-display">One payment, one checkout.</b> Your Kingdom Membership and books will be completed together in one secure payment.</p>
                  </div>
                )}
                <button onClick={checkout} disabled={busy} data-testid="cart-checkout"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-goldDark to-goldK text-white font-display font-extrabold py-4 text-base shadow-[0_6px_0_#B8860B] hover:-translate-y-0.5 active:translate-y-1 transition-transform disabled:opacity-60">
                  {busy ? <Loader2 size={18} className="animate-spin" /> : <Lock size={16} />} Continue to Checkout
                </button>
              </>
            ) : (
              <>
                {shopItems.length > 0 && (
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-ink/60 font-display font-bold">Subtotal</span>
                    <span className="font-display font-extrabold text-lg text-ink" data-testid="cart-subtotal">${(quote?.subtotal ?? shopSubtotal).toFixed(2)}</span>
                  </div>
                )}

                {shopItems.length > 0 && quote?.is_member && quote?.benefits_enabled && (
                  <div className="my-2 rounded-2xl bg-goldK/12 border border-goldK/30 p-3" data-testid="royalty-benefits">
                    <p className="font-display font-extrabold text-goldDark text-sm flex items-center gap-1.5"><Crown size={15} /> Kingdom Royalty Benefits</p>
                    {quote.discount > 0 && (
                      <div className="flex items-center justify-between text-sm mt-1.5 text-ink/75" data-testid="benefit-discount">
                        <span>Royalty discount ({quote.discount_percent}%)</span>
                        <span className="font-display font-bold text-tealK">−${quote.discount.toFixed(2)}</span>
                      </div>
                    )}
                    {quote.has_physical && (
                      <div className="flex items-center justify-between text-sm mt-1 text-ink/75" data-testid="benefit-shipping">
                        <span className="flex items-center gap-1"><Truck size={13} /> Standard shipping</span>
                        {quote.free_shipping_applied
                          ? <span className="font-display font-extrabold text-tealK">FREE</span>
                          : <span className="font-display font-bold text-ink">${quote.shipping.toFixed(2)}</span>}
                      </div>
                    )}
                    {quote.has_physical && !quote.free_shipping_applied && quote.free_shipping_remaining > 0 && (
                      <p className="text-[12px] text-goldDark mt-1.5 font-display font-bold" data-testid="free-ship-progress">👑 Only ${quote.free_shipping_remaining.toFixed(2)} more to unlock FREE Royalty shipping!</p>
                    )}
                    {quote.savings > 0 && (
                      <p className="text-sm text-tealK font-display font-extrabold mt-2" data-testid="royalty-savings">You saved ${quote.savings.toFixed(2)} today! 🎉</p>
                    )}
                  </div>
                )}

                {!quote?.is_member && quote?.potential_savings > 0 && (
                  <button onClick={() => { setOpen(false); nav("/membership"); }} data-testid="royalty-upsell"
                    className="my-2 w-full text-left rounded-2xl bg-purpleK/8 border border-purpleK/20 p-3 hover:bg-purpleK/12 transition">
                    <p className="font-display font-extrabold text-purpleK text-sm flex items-center gap-1.5"><Crown size={15} /> Become Kingdom Royalty</p>
                    <p className="text-[12px] text-ink/65 mt-0.5">Save <span className="font-bold text-ink">${quote.potential_savings.toFixed(2)}</span> on this order and enjoy unlimited Kingdom adventures.</p>
                  </button>
                )}

                {giftCount > 0 && (
                  <div className="flex items-center justify-between text-sm mt-1" data-testid="due-gift">
                    <span className="text-ink/70 flex items-center gap-1.5"><Gift size={14} className="text-pinkK" /> Gift membership{giftCount > 1 ? "s" : ""} ({giftCount})</span>
                    <span className="font-display font-bold text-ink">${giftSubtotal.toFixed(2)}</span>
                  </div>
                )}

                <div className="flex items-center justify-between pt-2 mt-1 border-t border-black/5">
                  <span className="text-ink/70 font-display font-extrabold">Total{(quote?.has_physical) ? "" : " (before tax)"}</span>
                  <span className="font-display font-extrabold text-2xl text-ink" data-testid="cart-total">${((quote ? quote.total : 0) + giftSubtotal).toFixed(2)}</span>
                </div>

                {giftCount > 0 && (
                  <p className="text-[12px] text-ink/55 my-2 flex items-start gap-1.5" data-testid="gift-note"><Gift size={13} className="text-pinkK shrink-0 mt-0.5" /> Your unique redemption code{giftCount > 1 ? "s" : ""} will appear after payment and be emailed to you — ready to give. 🎁</p>
                )}
                <p className="text-xs text-ink/45 my-2">Taxes {quote?.has_physical ? "" : "& shipping "}calculated at secure checkout. No account needed. 💛</p>
                <button onClick={checkout} disabled={busy} data-testid="cart-checkout"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-purpleK text-white font-display font-extrabold py-3.5 disabled:opacity-60">
                  {busy ? <Loader2 size={18} className="animate-spin" /> : <Lock size={16} />} Continue to Checkout
                </button>
                <PayPalCartCheckout onSuccess={onPaypalSuccess} />
                <p className="text-[11px] text-ink/40 text-center mt-2">Checkout as a guest or sign in — your choice.</p>
              </>
            )}
          </div>
        )}
      </aside>
    </div>
  );
}
